import Component, { loadComponent } from '../../../assets/scripts/modules/component.js'
import { getWatchedEpisodeIds } from '../../../assets/scripts/modules/localstorage.js'

const shuffleArray = arr => {
  let i = arr.length

  while (i !== 0) {
    const r = Math.floor(Math.random() * i)
    i--
    ;[arr[i], arr[r]] = [arr[r], arr[i]]
  }
}

const models = {
  character: {
    id: '',
    url: '',
    title: '',
    description: '',
    image: '',
    thumbnail: '',
    video: '',
    episodes: [],
    show_in_character_screen: false,
    micrio_tour_id: '',
  },
  episode: {
    id: '',
    url: '',
    pretitle: '',
    title: '',
    description: '',
    image: '',
    display_date: '',
    release_date: '',
    video_mark: '',
  },
  video: {
    portrait: '',
    landscape: '',
    release_date: '',
  },
}

class FetchDataComponent extends Component {
  init() {
    this.debug = false

    this.dataSources = {
      content: {
        type: 'static',
        src: '/static/mock-data/content.json',
        data: {},
      },
      micrio: {
        type: 'static',
        src: 'https://r2.micr.io/RzSCcjN/data/pub.json',
        data: {},
      },
    }

    this.data = {}

    window.addEventListener('init-after-load', event => this.initAfterLoad(event))
  }

  async initAfterLoad() {
    for (const [key, value] of Object.entries(this.dataSources)) {
      this.dataSources[key].data = await fetch(value.src)
        .then(response => response.ok && response.json())
        .catch(err => console.log(err))
    }

    this.parseDataSources()
    window.fetchData = this.data
    window.dispatchEvent(new CustomEvent('fetch-data', { detail: this.data }))
  }

  parseDataSources() {
    const content = this.dataSources.content.data
    const { episodesByTourId } = this.parseMicrioData(this.dataSources.micrio.data)
    const watchedEpisodeIds = getWatchedEpisodeIds()
    const characterLookup = Object.fromEntries(content.characters.map(character => [character.id, character]))
    let todayDate = new Date(Date.now())

    if (window.location.hostname.indexOf('0.0.0.0') !== -1 || window.location.hostname.indexOf('127.0.0.1') !== -1 || window.location.hostname.indexOf('localhost') !== -1 || window.location.hostname.indexOf('.fabriquehq.nl') !== -1) {
      todayDate = new Date('12-25-2030')
      this.debug = true
      console.warn('DATUM TESTCODE IS NOG ACTIEF! zie fetch-data.js')
    }

    this.data = { ...content }

    this.data.episodes = this.data.episodes.map(episode => ({ ...models.episode, ...episode }))
    this.data.characters = this.data.characters.map(character => ({ ...models.character, ...character }))
    this.data.videos = this.data.videos.map(video => ({ ...models.video, ...video }))

    // Mark if episodes are released or not relative to today
    this.data.episodes = this.data.episodes.map(episode => {
      if (this.debug) {
        episode.is_released = true
        episode.release_offset = 0

        return episode
      }

      episode.is_released = false
      episode.release_offset = 0

      const episodeDateString = episode.release_date?.split('-') || []

      if (!episodeDateString) {
        return episode
      }

      const episodeDate = new Date(episodeDateString[2], episodeDateString[1] - 1, episodeDateString[0])

      if (episodeDate > todayDate) {
        return episode
      }

      episode.release_offset = todayDate - episodeDate
      episode.is_released = true

      return episode
    })

    // Mark if characters are available based on if they have a released episode or not
    this.data.characters = this.data.characters.map(character => {
      if (this.debug) {
        character.is_available = true

        return character
      }

      const episodes = this.data.episodes.filter(episode => character.episodes.indexOf(episode.id) !== -1)
      const hasReleasedEpisode = episodes.some(episode => episode.is_released)

      character.is_available = !!hasReleasedEpisode

      return character
    })

    // Mark if episode is watched
    this.data.episodes = this.data.episodes.map(episode => {
      episode.watched = watchedEpisodeIds.includes(episode.id)

      const [, characterId, episodeNumber] = episode.id.match(/^(.+)-(\d+)$/) ?? []

      if (!characterId) {
        return episode
      }

      episode.character_id = characterId

      const characterMicrioTourId = characterLookup[characterId]?.micrio_tour_id

      if (characterMicrioTourId) {
        const micrioTour = episodesByTourId[characterMicrioTourId]

        if (micrioTour) {
          const micrioStory = micrioTour[Number(episodeNumber) - 1]

          if (micrioStory) {
            // can still be undefined if episode is not released yet
            episode.story = micrioStory

            if (!episode.image && episode.story?.id) {
              episode.image = `https://iiif.micr.io/${episode.story?.id}/max/%5E1920,1080/0/default.jpg`
            }
          } else {
            // This happens if the micrio story doesn't exist yet or isn't linked.
            //console.warn(`No micrio story found for character ${characterId} episode ${episodeNumber}`)
          }
        } else {
          // This happens when the hardcoded character ID doesn't exist (any longer).
          console.warn(`No micrio tour found for character ${characterId}`)
        }
      }

      if (episode.story?.thumbnailUrl) {
        episode.image = episode.story?.thumbnailUrl
      }

      if (window.location.hostname.indexOf('0.0.0.0') !== -1 || window.location.hostname.indexOf('127.0.0.1') !== -1 || window.location.hostname.indexOf('localhost') !== -1 || window.location.hostname.indexOf('.fabriquehq.nl') !== -1) {
        episode.is_released = true

        return episode
      }

      // Even if it's past the release date, an episode without content cannot be clickable and therefore not released
      if (!episode.story) {
        episode.is_released = false
        return episode
      }

      return episode
    })

    // Mark if videos are released or not relative to today
    this.data.videos = this.data.videos.map(video => {
      if (this.debug) {
        video.is_released = true
        video.release_offset = 0

        return video
      }

      video.is_released = false
      video.release_offset = 0

      const todayDate = new Date(Date.now())
      const episodeDateString = video.release_date?.split('-') || []
      const episodeDate = new Date(episodeDateString[2], episodeDateString[1] - 1, episodeDateString[0])

      if (episodeDate > todayDate) {
        return video
      }

      video.release_offset = todayDate - episodeDate
      video.is_released = true

      return video
    })

    // To make sure we don't rely on incidental order in the array
    shuffleArray(this.data.episodes)
    shuffleArray(this.data.characters)
    shuffleArray(this.data.videos)

    //console.log(this.data)
  }

  parseMicrioData(micrioData) {
    const markerLookup = Object.fromEntries(micrioData.markers.map(marker => [marker.id, marker]))

    const getEpisodesForTour = tour => {
      return tour.steps
        .map(step => {
          // tour step will have format "390d29ef-bec4-c0be-2dc0-bf9981cc88dd,ojTWcyL" (if it's a regular tour starting from image ojTWcyL)
          // or format "79aa282f-db78-4e71-b0f3-c7f69666220c" (which is a local marker, indicating a video element which we need to get from the marker lookup)
          const stepParts = step.split(',')

          if (stepParts.length === 2) {
            return { type: 'micrio', id: stepParts[1] }
          }

          const localMarker = markerLookup[step]
          const embedUrl = localMarker && Object.values(localMarker.i18n)[0].embedUrl

          if (!embedUrl) {
            // we don't know how to handle a marker without an embedUrl so just ignore it
            return null
          }

          const thumbnailUrl = localMarker.images?.[0]?.src

          return { type: 'video', url: embedUrl, thumbnailUrl }
        })
        .filter(episode => !!episode)
    }

    /** Type is Record<string, ({type: 'video', url: string}|{type: 'micrio', id: string})> */
    const episodesByTourId = Object.fromEntries(micrioData.markerTours.map(tour => [tour.id, getEpisodesForTour(tour)]))

    return { episodesByTourId }
  }
}

window.addEventListener('init-load', () => loadComponent(document, 'fetch-data', FetchDataComponent))
